import { EditOutlined } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    LinearProgress,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import OrganizationDetailForm from '../components/organizationDetailForm';
import OrganizationProfileDetails from '../components/organizationProfileDetails';
import OrganizationSelector from '../components/organizationSelector';
import PageHeader from 'components/v2/common/pageHeader';

import api from 'api';
import { ACTIONS } from 'constant';
import { clientServiceProviderKeys } from 'hooks/useClientServiceProviders';
import useModalObj from 'hooks/useModalObj';
import { organizationProfileKeys, useOrganizationProfile } from 'hooks/useOrganizationProfile';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { OrganizationInterface } from 'types';

const REQUIRED_ORG_FIELDS: (keyof OrganizationInterface)[] = [
    'company_name',
    'legal_entity_name',
    'region',
    'address',
    'organization_type',
    'date_established',
    'industry',
    'number_employees',
    'number_canadian_employees',
    'annual_revenue',
];

const countCompletedFields = (profile: OrganizationInterface | null): number => {
    if (!profile) return 0;
    return REQUIRED_ORG_FIELDS.filter((field) => {
        const value = profile[field];
        return value !== null && value !== undefined && value !== '';
    }).length;
};

const ProfileSkeleton = () => (
    <Stack spacing={2}>
        <Skeleton variant="text" width={180} height={36} />
        {[0, 1].map((section) => (
            <Box key={section}>
                <Skeleton variant="text" width={220} height={28} sx={{ mb: 2 }} />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        columnGap: 4,
                        rowGap: 2.5,
                    }}
                >
                    {Array.from({ length: section === 0 ? 6 : 5 }).map((_, index) => (
                        <Skeleton key={index} variant="text" height={40} />
                    ))}
                </Box>
            </Box>
        ))}
    </Stack>
);

const OrganizationProfileForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { translate } = useTranslate();
    const { id } = useParams();

    const isOnboarding = location.state?.onboarding === true;

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<OrganizationInterface>();
    const { data, isLoading } = useOrganizationProfile(id, { alertOnError: true });
    const organizationProfile = data ?? null;

    const completedCount = useMemo(
        () => countCompletedFields(organizationProfile),
        [organizationProfile]
    );
    const totalRequired = REQUIRED_ORG_FIELDS.length;
    const profileComplete = completedCount === totalRequired;

    const handleAction = (action: ACTIONS) => {
        switch (action) {
            case ACTIONS.EDIT:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'editProfile',
                    data: organizationProfile,
                    disabled: false,
                    loading: false,
                }));
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (!isOnboarding) return;
        handleAction(ACTIONS.EDIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (values: OrganizationInterface) => {
        if (!id) return;

        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));

        try {
            const editing = organizationProfile?.industry;

            const response = editing
                ? await api.clientServiceProviders.updateOrganizationProfile(id, values)
                : await api.clientServiceProviders.createOrganizationProfile(id, values);

            queryClient.setQueryData(organizationProfileKeys.byServiceProvider(id), response.data);
            void queryClient.invalidateQueries({ queryKey: clientServiceProviderKeys.all });
            dispatch(setAlert({ message: 'saveChanges', type: 'success' }));
            onResetModalObj();

            if (isOnboarding && countCompletedFields(response.data) === totalRequired) {
                navigate('/dashboard', { replace: true });
            }
        } catch {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    if (isLoading) {
        return <ProfileSkeleton />;
    }

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <PageHeader title="clientProfile">
                <OrganizationSelector />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
                    onClick={() => handleAction(ACTIONS.EDIT)}
                >
                    {translate('edit')}
                </Button>
            </PageHeader>
            {isOnboarding && !profileComplete && (
                <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    <AlertTitle sx={{ fontWeight: 600 }}>
                        {translate('Complete your organization profile')}
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {translate(
                            'Please fill in all required fields below to get started. You can access the dashboard once your profile is complete.'
                        )}
                    </Typography>
                    <Stack spacing={0.75}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                                {completedCount} {translate('of')} {totalRequired}{' '}
                                {translate('requiredFieldsCompleted')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {Math.round((completedCount / totalRequired) * 100)}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={(completedCount / totalRequired) * 100}
                            sx={{ height: 6, borderRadius: 3 }}
                        />
                    </Stack>
                </Alert>
            )}
            <OrganizationProfileDetails profile={organizationProfile} />
            {modalObject.action === ACTIONS.EDIT && (
                <OrganizationDetailForm
                    open
                    initialValues={organizationProfile}
                    disabled={modalObject.disabled || modalObject.loading}
                    onSubmit={handleSubmit}
                    onClose={onResetModalObj}
                />
            )}
        </Stack>
    );
};

export default OrganizationProfileForm;
